import React, { useState, useEffect } from "react";
import {
  Grid, Typography, Paper, Box, Button,
  FormControl, InputLabel, Select, MenuItem,
  TextField,
  Tabs, Tab,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TableSortLabel, TablePagination,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useMember } from '../context/MemberProfileContext';
import { fetchBalaviharReportTypes, runReport } from '../api/reportServices';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

const navy = '#1A3A6B';

const PARAM_MAP = { Balavihar: 'bv', Event: 'events', BOD: 'bod', Finance: 'finance' };

export default function Reports() {
  const { accessToken, signOut } = useAuth();
  const { memberProfile } = useMember();
  const navigate = useNavigate();

  const reportsMenu = memberProfile.menus.find(m => m.text === 'Reports');
  const reportTypes = reportsMenu && Array.isArray(reportsMenu.children)
    ? reportsMenu.children.map(c => c.text)
    : [];

  const [selectedType, setSelectedType] = useState('');
  const [reportParam, setReportParam] = useState('');
  const [bvReportTypes, setBvReportTypes] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('');
  const [reportConditions, setReportConditions] = useState([]);
  const [selectedValues, setSelectedValues] = useState({});
  const [reportData, setReportData] = useState({ reportId: '', params: {} });
  const [reportResult, setReportResult] = useState(null);

  // Sort / pagination state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!accessToken) { signOut(); navigate('/'); return; }
    if (reportParam) fetchBvReportTypes();
  }, [accessToken, reportParam]);

  const selectedTypeObj = bvReportTypes.find(t => t.name === selectedReportType);

  const buildParams = (values, conditions) => {
    const params = {};
    conditions.forEach(cond => {
      const val = values[cond.conditionKey] ?? '';
      if (cond.displayType === 'textInput') {
        params[cond.conditionKey] = val.trim() === '' ? '%%' : `%${val.trim()}%`;
      } else {
        params[cond.conditionKey] = val;
      }
    });
    return params;
  };

  useEffect(() => {
    if (selectedTypeObj) {
      const conditions = selectedTypeObj.reportConditions || [];
      setReportConditions(conditions);

      const currentYear = String(new Date().getFullYear());
      const defaults = {};
      conditions.forEach(cond => {
        const label = (cond.displayText || '').toLowerCase();
        const vals = cond.conditionValues ? cond.conditionValues.split(',').map(v => v.trim()) : [];
        if (cond.displayType === 'textInput') {
          defaults[cond.conditionKey] = '';
        } else if (label.includes('year')) {
          defaults[cond.conditionKey] = vals.includes(currentYear) ? currentYear : (vals[0] || '');
        } else if (label.includes('session')) {
          defaults[cond.conditionKey] = 'All Sessions';
        } else if (label.includes('grade')) {
          defaults[cond.conditionKey] = 'All';
        } else {
          defaults[cond.conditionKey] = vals[0] || '';
        }
      });
      setSelectedValues(defaults);
      setReportData({ reportId: selectedTypeObj.reportId, params: buildParams(defaults, conditions) });
    } else {
      setReportConditions([]);
      setReportData({ reportId: '', params: {} });
    }
  }, [selectedTypeObj]);

  const handleTabChange = (_, type) => {
    setSelectedType(type);
    setReportParam(PARAM_MAP[type] || '');
    setReportResult(null);
    setSelectedReportType('');
    setSelectedValues({});
    setReportConditions([]);
    setReportData({ reportId: '', params: {} });
    setPage(0);
  };

  const handleReportTypeChange = (e) => {
    setSelectedReportType(e.target.value);
    setReportConditions([]);
    setReportData({ reportId: '', params: {} });
    setReportResult(null);
  };

  const handleDropdownChange = (key, value) => {
    const updated = { ...selectedValues, [key]: value };
    setSelectedValues(updated);
    setReportData(prev => ({ ...prev, params: buildParams(updated, reportConditions) }));
  };

  const handleTextChange = (key, value) => {
    const updated = { ...selectedValues, [key]: value };
    setSelectedValues(updated);
    setReportData(prev => ({ ...prev, params: buildParams(updated, reportConditions) }));
  };

  const fetchBvReportTypes = async () => {
    try {
      const { response, data } = await fetchBalaviharReportTypes(accessToken, reportParam);
      if (response.ok) setBvReportTypes(data);
    } catch (e) {
      console.error('Error fetching report types:', e);
    }
  };

  const handleRunReport = async (e) => {
    e.preventDefault();
    try {
      const { response, data } = await runReport(accessToken, reportData, reportParam);
      if (response.ok) {
        setReportResult(data);
        setOrderBy('');
        setPage(0);
      }
    } catch (e) {
      console.error('Error running report:', e);
    }
  };

  const handleDownload = () => {
    const ws = XLSX.utils.json_to_sheet(reportResult.datas, { header: reportResult.headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'report.xlsx');
  };

  const handleSort = (header) => {
    const isAsc = orderBy === header && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(header);
    setPage(0);
  };

  const rows = reportResult?.datas || [];
  const sortedRows = orderBy
    ? [...rows].sort((a, b) => {
        const av = a[orderBy] ?? '';
        const bv = b[orderBy] ?? '';
        if (bv < av) return order === 'desc' ? -1 : 1;
        if (bv > av) return order === 'desc' ? 1 : -1;
        return 0;
      })
    : rows;
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const ticketSummary = (() => {
    if (!reportResult?.headers?.includes('Tickets Purchased')) return null;
    let sold = 0, pending = 0, cancelled = 0, total = 0;
    rows.forEach(row => {
      const qty = parseInt(row['Tickets Purchased']) || 0;
      const status = (row['Status'] || '').toUpperCase();
      total += qty;
      if (status === 'ACTIVE') sold += qty;
      else if (status === 'PENDING') pending += qty;
      else if (status === 'CANCELLED') cancelled += qty;
    });
    return { sold, pending, cancelled, total };
  })();

  return (
    <Box sx={{ width: '100%' }}>
      {/* Page title */}
      <Typography variant="h6" align="center" gutterBottom sx={{ fontFamily: 'Georgia, serif', color: navy, mb: 2 }}>
        CMDFW Report System
      </Typography>

      {/* Section tabs */}
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={selectedType || false}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: '1px solid #e0e0e0',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minWidth: 100 },
            '& .Mui-selected': { color: `${navy} !important` },
            '& .MuiTabs-indicator': { bgcolor: navy },
          }}
        >
          {reportTypes.map(type => (
            <Tab key={type} label={type} value={type} />
          ))}
        </Tabs>
      </Paper>

      {/* Filter form — only shown once a tab is selected */}
      {reportParam && (
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <form onSubmit={handleRunReport}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Report Type</InputLabel>
                  <Select
                    required
                    value={selectedReportType}
                    label="Report Type"
                    onChange={handleReportTypeChange}
                  >
                    {bvReportTypes.map((t, i) => (
                      <MenuItem key={i} value={t.name}>{t.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {reportConditions.map((cond, i) => (
                <Grid item xs={12} sm={cond.conditionKey === 'search' ? 12 : 4} key={i}>
                  {cond.displayType === 'textInput' ? (
                    <TextField
                      fullWidth
                      size="small"
                      label={cond.displayText}
                      value={selectedValues[cond.conditionKey] || ''}
                      onChange={e => handleTextChange(cond.conditionKey, e.target.value)}
                      placeholder="Leave blank to match all"
                    />
                  ) : (
                    <FormControl fullWidth size="small">
                      <InputLabel>{cond.displayText}</InputLabel>
                      <Select
                        required
                        value={selectedValues[cond.conditionKey] || ''}
                        label={cond.displayText}
                        onChange={e => handleDropdownChange(cond.conditionKey, e.target.value)}
                      >
                        {[
                          ...(cond.displayText?.toLowerCase().includes('session')
                            ? [{ v: 'All Sessions', l: 'All Sessions' }] : []),
                          ...(cond.displayText?.toLowerCase().includes('grade')
                            ? [{ v: 'All', l: 'All Grades' }] : []),
                          ...(cond.conditionValues
                            ? cond.conditionValues.split(',').map(v => ({ v: v.trim(), l: v.trim() }))
                            : []),
                        ].map((opt, vi) => (
                          <MenuItem key={vi} value={opt.v}>{opt.l}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button type="submit" variant="contained" sx={{ bgcolor: navy, '&:hover': { bgcolor: '#122c55' }, textTransform: 'none', fontWeight: 600 }}>
                    Run Report
                  </Button>
                  {reportResult && (
                    <Button variant="outlined" onClick={handleDownload} sx={{ textTransform: 'none', fontWeight: 600, borderColor: navy, color: navy }}>
                      Download Excel
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}

      {/* Results table */}
      {reportResult && (
        <Paper elevation={3} sx={{ overflow: 'hidden', width: '100%' }}>
          <Box sx={{ bgcolor: navy, px: 3, py: 1.5 }}>
            <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1rem', fontFamily: 'Georgia, serif' }}>
              {selectedReportType} — {selectedType} Report
            </Typography>
          </Box>

          {ticketSummary && (
            <Box sx={{ display: 'flex', gap: 4, px: 3, py: 2, borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
              {[
                { label: 'Sold Tickets', value: ticketSummary.sold, color: '#2e7d32' },
                { label: 'Pending', value: ticketSummary.pending, color: '#e65100' },
                { label: 'Cancelled', value: ticketSummary.cancelled, color: '#c62828' },
                { label: 'Total Tickets', value: ticketSummary.total, color: navy },
              ].map(({ label, value, color }) => (
                <Box key={label} sx={{ textAlign: 'center', minWidth: 90 }}>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 700, color, lineHeight: 1.2 }}>{value}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.3 }}>{label}</Typography>
                </Box>
              ))}
            </Box>
          )}

          {rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No data available for the selected report.</Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 1000 }} size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e4eaf0' }}>
                      {reportResult.headers.map((h) => (
                        <TableCell key={h} sortDirection={orderBy === h ? order : false}
                          sx={{ fontWeight: 700, color: navy, whiteSpace: 'nowrap' }}>
                          <TableSortLabel
                            active={orderBy === h}
                            direction={orderBy === h ? order : 'asc'}
                            onClick={() => handleSort(h)}
                            sx={{
                              '&.Mui-active': { color: navy },
                              '& .MuiTableSortLabel-icon': { color: `${navy} !important` },
                            }}
                          >
                            {h.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleRows.map((row, ri) => (
                      <TableRow key={ri}
                        sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f4f8' } }}>
                        {reportResult.headers.map((h) => (
                          <TableCell key={h} sx={{ whiteSpace: 'nowrap' }}>{row[h]}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[10, 20, 50, 100]}
              />
            </>
          )}
        </Paper>
      )}
    </Box>
  );
}
